import { LeaderboardAuthSession } from './leaderboard_auth_session.js';
import { GlobalSettings } from '../../global_settings.js';
import { addScores, setUserSave } from './leaderboard.requests.js';
import { getLeaderboardPlayer } from './leaderboard_player_context.js';
import { logLeaders } from './leaderboard_http_client.js';

const TIMEOUT_MS = 10_000;

interface LeaderboardAuthResponse
{
  accessToken: string;
  userId: number;
  errorMessage?: string | null;
  time?: number;
}

function isNativePlatform(): boolean
{
  try
  {
    const platform = ( globalThis as any )?.Capacitor?.getPlatform?.();
    return platform === 'android' || platform === 'ios';
  }
  catch
  {
    return false;
  }
}

async function postJson( url: string, body: unknown ): Promise<{ status: number; data: unknown }>
{
  if ( isNativePlatform() )
  {
    try
    {
      const http: any = ( globalThis as any )?.Capacitor?.Plugins?.CapacitorHttp;
      if ( typeof http?.request === 'function' )
      {
        const res = await http.request( {
          url,
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          data: body,
          connectTimeout: TIMEOUT_MS,
          readTimeout: TIMEOUT_MS,
        } );
        return { status: res.status, data: res.data };
      }
    }
    catch ( e )
    {
      console.warn( '[LeaderboardAuth] CapacitorHttp failed, falling back to fetch:', String( e ) );
    }
  }

  const controller = new AbortController();
  const timer = setTimeout( () => controller.abort(), TIMEOUT_MS );
  try
  {
    const res = await fetch( url, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify( body ),
      signal: controller.signal,
    } );
    const data = await res.json().catch( () => null );
    return { status: res.status, data };
  }
  catch ( e )
  {
    throw new Error( `Network error: ${e}` );
  }
  finally
  {
    clearTimeout( timer );
  }
}

let _sessionLoggedIn = false;
// The menu prefetch, the leaderboard screen and the score sync can all ask to log in at once.
let login_in_flight: Promise<void> | null = null;
// Board registration of the last login. Not awaited by login(): a read of the board does not
// need it, only a request that must see the player's own row waits for it (registered()).
let registration: Promise<void> = Promise.resolve();

export const LeaderboardAuthService = {
  login(): Promise<void>
  {
    if ( !login_in_flight )
      login_in_flight = this.loginOnce().finally( () => { login_in_flight = null; } );
    return login_in_flight;
  },

  /** Resolves once the player is on the board (or registration failed), never rejects. */
  registered(): Promise<void>
  {
    return registration;
  },

  async loginOnce(): Promise<void>
  {
    _sessionLoggedIn = false;
    const player = getLeaderboardPlayer();
    const game_player_id: string | null = player ? player.id : null;

    if ( !game_player_id )
    {
      throw new Error( '[LeaderboardAuth] Cannot login — no game player ID available' );
    }

    const body = { Did: game_player_id, Platform: 0, IsQA: false, BN: 0 };
    console.log( `[LeaderboardAuth] Login request:`, body );

    const authUrl = `${GlobalSettings.LEADERBOARD_BASE_URL}/auth/api/auth`;
    const res = await postJson( authUrl, body );

    if ( res.status < 200 || res.status >= 300 )
    {
      throw new Error( `[LeaderboardAuth] Login failed: ${res.status} — ${JSON.stringify( res.data )}` );
    }

    const data = res.data as LeaderboardAuthResponse;
    logLeaders( 'auth/api/auth', { ...data, accessToken: data.accessToken ? data.accessToken.slice( 0, 12 ) + '...' : data.accessToken } );
    LeaderboardAuthSession.set( { access_token: data.accessToken, userId: data.userId, game_player_id } );
    _sessionLoggedIn = true;
    console.log( `[LeaderboardAuth] Logged in as userId=${data.userId}, game_player_id=${game_player_id}` );

    registration = addScores( `global_${player?.content_mode ?? '0'}`, 0 )
      .catch( e => console.warn( '[LeaderboardAuth] Board registration failed', e ) );

    const fb_url = player?.facebook_avatar_url ?? undefined;
    setUserSave( player?.level ?? 1, player?.nickname ?? '', player?.avatar_id ?? 0, fb_url ?? undefined )
      .catch( e => console.warn( '[LeaderboardAuth] setUserSave after login failed', e ) );
  },

  async ensureLoggedIn(): Promise<boolean>
  {
    if ( _sessionLoggedIn ) return false;
    await this.login();
    return true;
  },

  resetSession(): void
  {
    _sessionLoggedIn = false;
    LeaderboardAuthSession.clear();
  },
};
